/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  H2,
  Input,
  InputPassword,
  LineDivider,
  Image
} from '../components';
import { login as loginAction, socialLogin as socialLoginAction } from '../lib/redux';
import { ROUTES, ERROR_MESSAGES } from '../Constants';
import facebook from '../assets/images/facebook-logo.svg';
import {
  initializeSession as initializeSessionAction,
  redirectToFirstPage
} from '../lib/redux/actions/app';

const LoginContainer = styled.form`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AlertContainer = styled.div`
  margin-bottom: 8px;
  min-height: 40px;
  width: 100%;
`;

function isEmailValid(email) {
  if (email) {
    return true;
  }

  return false;
}

function renderAlert(error) {
  switch (error) {
    case ERROR_MESSAGES.ACCESS_DENIED_EXCEPTION:
    case ERROR_MESSAGES.INVALID_CREDENTIALS_EXCEPTION:
      return (
        <Alert data-test="error-alert" error>
          These credentials aren&apos;t quite right. Please try again (must be designated
          &quot;Agent&quot; to enter).
        </Alert>
      );
    case ERROR_MESSAGES.USER_STATUS_HAS_CHANGED_EXCEPTION:
      return (
        <Alert data-aat="error-alert" error>
          There's been a change to your agent status. For more information, contact support.
        </Alert>
      );

    default:
      return null;
  }
}

export function Login({
  login,
  socialLogin,
  history,
  isAuthenticated,
  isLoading,
  initializeSession,
  errorMessage
}) {
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState(errorMessage);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const isValid = !!password.trim() && !!username.trim();

  async function onClickSocialLogin(fbData) {
    try {
      await socialLogin(fbData.accessToken);
      await initializeSession(history);
    } catch (err) {
      return setError(ERROR_MESSAGES.INVALID_CREDENTIALS_EXCEPTION);
    }

    return redirectToFirstPage(history);
  }

  const onClick = useCallback(
    async event => {
      event.preventDefault();

      const onLogin = async () => {
        if (!isEmailValid(username)) {
          return setIsEmailInvalid(true);
        }

        setError(false);
        try {
          await login(username, password);

          await initializeSession(history);
        } catch (err) {
          if (!err || !err.reason) {
            return setError(ERROR_MESSAGES.INVALID_CREDENTIALS_EXCEPTION);
          }

          return setError(err.reason);
        }

        return redirectToFirstPage(history);
      };

      await onLogin();
    },
    [username, password]
  );

  useEffect(() => {
    if (isAuthenticated) {
      history.push(ROUTES.HOME);
    }
  }, []);

  return (
    <LoginContainer onSubmit={onClick}>
      <Card>
        <H2 margin={{ bottom: 'xs' }} fontWeight="bold">
          Log In
        </H2>

        <AlertContainer>{renderAlert(error)}</AlertContainer>

        <Input
          autoFocus
          error={isEmailInvalid}
          errorText="Please enter a valid email"
          label="Email"
          data-test="username-input"
          onBlur={() => {
            if (!isEmailValid(username)) {
              setIsEmailInvalid(true);
              return;
            }
            setIsEmailInvalid(false);
          }}
          onChange={value => setUsername(value)}
          name="username"
          placeholder="name@mail.com"
          value={username}
        />

        <InputPassword
          label="Password"
          data-test="password-input"
          onChange={value => setPassword(value)}
          name="password"
          value={password}
        />

        <Checkbox
          onToggle={() => setRememberMe(!rememberMe)}
          checked={rememberMe}
          data-test="remember-me-checkbox"
          label="Remember this browser?"
        />

        <Button
          onClick={onClick}
          data-test="submit-button"
          margin={{ top: 'large' }}
          disabled={!isValid || isLoading}
          type="submit"
        >
          Log in
        </Button>
        <LineDivider>or</LineDivider>
        <FacebookLogin
          disableMobileRedirect
          data-test="social-login-button"
          appId={window._env_.FACEBOOK_APP_ID}
          callback={values => onClickSocialLogin(values)}
          render={renderProps => (
            <Button
              data-test="button-social-login"
              onClick={renderProps.onClick}
              disabled={isLoading}
              theme="facebook"
              type="button"
            >
              <Image src={facebook} isIcon margin={{ right: 'xs' }} />
              Continue with Facebook
            </Button>
          )}
        />
      </Card>
    </LoginContainer>
  );
}

Login.propTypes = {
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  initializeSession: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  login: PropTypes.func.isRequired,
  socialLogin: PropTypes.func.isRequired
};

function mapStateToProps({ me, session, agent }) {
  return {
    isAuthenticated: session.isAuthenticated,
    isLoading: me.isLoading || agent.isLoading,
    errorMessage: me.loginErrorMessage
  };
}

export default connect(
  mapStateToProps,
  {
    socialLogin: socialLoginAction,
    login: loginAction,
    initializeSession: initializeSessionAction
  }
)(Login);
